import httpStatus from "http-status";
import Joi from "joi";
import lodash from "lodash";
import qs from "qs";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ActionFunctionArgs, LoaderFunctionArgs, MetaFunction } from "react-router";
import { Form, Link, useFetcher, useLocation } from "react-router";
import PasswordField from "~/components/Fields/PasswordField";
import TextField from "~/components/Fields/TextField";
import * as cookies from "~/cookies";
import AuthLayout, { AuthSwitch } from "~/design/auth/AuthLayout";
import Button from "~/design/ui/Button";
import i18nServer from "~/i18n.server";
import { postLogin } from "~/services/api";
import to from "~/utils/to";
import toLatinDigits from "~/utils/toLatinDigits";

const schema = Joi.object({
  identifier: Joi.string().required().messages({ "string.empty": "errors.string.empty" }),
  password: Joi.string().required(),
});

export async function loader({ request }: LoaderFunctionArgs) {
  await cookies.auth.unrequired(request);
  const locale = await i18nServer.getLocale(request);
  const params = qs.parse(new URL(request.url).search, { ignoreQueryPrefix: true });
  const t = await i18nServer.getFixedT(request);

  return {
    params,
    strings: { title: t("login") + " | " + t("title"), description: t("description"), og: { title: t("login") + " | " + t("og.title") } },
    locale,
  };
}

export const meta: MetaFunction = ({ data }) => {
  return [
    { title: data.strings.title, description: data.strings.description },
    { name: "description", content: data.strings.description },
    { property: "og:title", content: data.strings.og.title },
  ];
};

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const values = Object.fromEntries(formData);
  values.identifier = toLatinDigits(values.identifier);
  let errors: { [key: string]: string } = {};

  schema.validate(values, { abortEarly: false }).error?.details.forEach((detail) => {
    errors[detail.path.join(".")] = `errors.${detail.type}`;
  });
  if (!lodash.isEmpty(errors)) return Response.json({ errors, values });

  const [err, res] = await to(
    postLogin({
      data: values,
      headers: { "axios-to-fetch": 1, "user-agent": request.headers.get("user-agent"), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
    }),
  );
  if (err) {
    if (err.status === httpStatus["FORBIDDEN"]) {
      errors = { identifier: "auth.login.invalid", password: "auth.login.invalid" };
    } else if (err.status === httpStatus["LOCKED"]) {
      errors = { password: "auth.login.locked" };
    } else throw err;
    if (!lodash.isEmpty(errors)) return Response.json({ errors, values });
  }

  return cookies.auth.login(request, res.data);
}

function Login() {
  const { t } = useTranslation();
  const location = useLocation();
  const fetcher = useFetcher();
  // Use fetcher.data instead of actionData when using fetcher.submit
  const formData = fetcher.data;
  const errors = (formData as any)?.errors ?? {};
  const submitting = fetcher.state === "submitting";
  const [identifier, setIdentifier] = useState((formData as any)?.values?.identifier || "");
  const form = useRef<HTMLFormElement>(null);
  // after a failed submit, move keyboard focus to the first field to fix
  useEffect(() => {
    form.current?.querySelector<HTMLInputElement>("[aria-invalid=true]")?.focus();
  }, [fetcher.data]);

  const normalizeIdentifier = (value: string) => {
    if (!value) return value;

    const onlyNumbers = value.replace(/[^\d]/g, "");
    if (onlyNumbers[0] === "0") return onlyNumbers.slice(1);
    if (onlyNumbers[0] !== "9") return onlyNumbers.slice(1);
    else return onlyNumbers.slice(0, 10);
  };

  const handleIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const normalizedValue = normalizeIdentifier(toLatinDigits(e.target.value));
    setIdentifier(normalizedValue);
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    const form = e.currentTarget;

    const formData = new FormData(form);
    fetcher.submit(formData, { method: "POST", action: "/login" + location.search });
  };

  return (
    <AuthLayout
      title={t("ui.auth.login.title")}
      subtitle={t("ui.auth.login.subtitle")}
      alt={<AuthSwitch text={t("ui.auth.login.switchText")} cta={t("ui.auth.login.switchCta")} to={"/register" + location.search} />}
    >
      <Form ref={form} onSubmit={onSubmit} noValidate>
        <fieldset className="flex flex-col gap-5">
          <TextField
            label={{ text: t("ui.auth.fields.mobile") }}
            prefix={{ text: "+98" }}
            input={{
              type: "tel",
              name: "identifier",
              value: identifier,
              onChange: handleIdentifierChange,
              inputMode: "numeric",
              autoComplete: "tel-national",
              className: "ltr-iso font-display tracking-wider",
            }}
            error={errors.identifier}
            // a wrong mobile/password pair is reported once, under the password
            errorMode={errors.identifier === "auth.login.invalid" ? "none" : "subtitle"}
          />
          <div className="flex flex-col gap-1">
            <PasswordField
              label={{ text: t("ui.auth.fields.password") }}
              input={{ name: "password", defaultValue: (formData as any)?.values?.password, autoComplete: "current-password" }}
              error={errors.password}
            />
            <Link
              to="/forgot"
              prefetch="intent"
              className="inline-flex min-h-11 items-center self-start text-[15px] font-medium text-accent-text underline-offset-[6px] hover:underline"
            >
              {t("ui.auth.login.forgot")}
            </Link>
          </div>
          <Button type="submit" size="lg" block aria-disabled={submitting || undefined}>
            {submitting ? t("wait") : t("login")}
          </Button>
        </fieldset>
      </Form>
    </AuthLayout>
  );
}

export default Login;
